import { useEffect } from "react";
import Hammer from "hammerjs";

const useZoomControl = ({
  mountRef,
  cameraRef,
  isZooming,
}: {
  mountRef: React.MutableRefObject<HTMLDivElement | null>;
  cameraRef: React.MutableRefObject<THREE.PerspectiveCamera | null>;
  isZooming: React.MutableRefObject<boolean>;
}) => {
  const minFOV = 30; // More zoomed in
  const maxFOV = 75; // Initial FOV, less zoomed in

  // useEffect to handle zooming in and out via mouse wheel
  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      const zoomSensitivity = -0.05;
      if (cameraRef.current) {
        // Adjust the camera's FOV and clamp it between minFOV and maxFOV
        cameraRef.current.fov = Math.max(
          minFOV,
          Math.min(
            maxFOV,
            cameraRef.current.fov + event.deltaY * zoomSensitivity,
          ),
        );
        cameraRef.current.updateProjectionMatrix();
      }
    };

    const container = mountRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel);
    }

    return () => {
      if (container) {
        container.removeEventListener("wheel", handleWheel);
      }
    };
  }, [cameraRef, mountRef]);

  // useEffect to handle pinch zooming on mobile
  useEffect(() => {
    if (!cameraRef.current) return;

    // Set up Hammer.js for pinch zoom
    if (mountRef.current) {
      const manager = new Hammer.Manager(mountRef.current);
      const pinch = new Hammer.Pinch();
      manager.add(pinch);

      let initialFOV = cameraRef.current.fov;

      manager.on("pinchstart", () => {
        if (!cameraRef.current) return;
        initialFOV = cameraRef.current.fov; // Store the initial FOV at the start of pinch
        isZooming.current = true; // Set zooming state when pinch gesture starts
      });

      manager.on("pinch", (e) => {
        if (!cameraRef.current) return;

        const pinchScale = e.scale; // Scale factor of the pinch gesture
        const newFOV = initialFOV / pinchScale; // Calculate new FOV based on pinch
        cameraRef.current.fov = Math.max(minFOV, Math.min(maxFOV, newFOV));
        cameraRef.current.updateProjectionMatrix();
      });

      manager.on("pinchend", () => {
        isZooming.current = false; // Reset zooming state when pinch gesture ends
      });
    }
  }, [cameraRef, mountRef, isZooming]);

  return { isZooming };
};

export default useZoomControl;
